import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router";
// @ts-expect-error FIXME: No types available for @turf/helpers
import { AllGeoJSON } from "@turf/helpers";

import { selectVersionConfig } from "fond/api";
import { LayoutContext } from "fond/layout/LayoutProvider";
import { selectWidget } from "fond/layout/widgets";
import { MapContext } from "fond/map/MapProvider";
import { mergeConfigWithOverlays, useOverlaysWithConfigs } from "fond/map/Overlays/useOverlaysWithConfigs";
import { getCurrentProject } from "fond/project";
import CommentFeatureEditor from "fond/project/comments/CommentFeatureEditor";
import CommentPopup from "fond/project/comments/CommentPopup";
import { selectFeature } from "fond/project/redux";
import { getOne, selectComment, setFilters, setSearchText } from "fond/redux/comments";
import * as turf from "fond/turf";
import { Configuration, EditMode, Store, Widget } from "fond/types";
import { LayerConfig, SublayerConfig } from "fond/types/ProjectLayerConfig";
import { useAppDispatch } from "fond/utils/hooks";

import FeaturesPopup from "./featureSelection/FeaturesPopup";

interface RouteParams {
  /**
   * UUID of the current project
   */
  uuid: string;
  /**
   * The type of selection
   */
  type: "comment" | "feature";
  /**
   *  UUID of the selected comment
   */
  id: string;
}

interface IProps {
  layerConfigs: Array<LayerConfig | SublayerConfig>;
  editMode: EditMode;
  onClose(): void;
}

/**
 * A wrapper component for displaying the Feature/Comment related popups on the map &
 * monitoring / handling the URL changes relayed to popup selection.
 */
const FeatureHandler: React.FC<IProps> = ({ editMode, layerConfigs, onClose }: IProps) => {
  const { type, id, uuid } = useParams<keyof RouteParams>() as RouteParams;
  const { map } = useContext(MapContext);
  const layout = useContext(LayoutContext);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const selectedComment = useSelector((state: Store) => state.project.selectedComment);
  const featureSelectionPopup = useSelector((state: Store) => state.project.featureSelectionPopup);
  const versionId = useSelector((state: Store) => state.project.versionId);
  const project = useSelector((state: Store) => getCurrentProject(state.project));
  const config = useSelector((state: Store) => selectVersionConfig(state, versionId));
  const editId = useSelector((state: Store) => state.comments.editId);
  const comment = useSelector((state: Store) => getOne(state)(id));
  const [init, setInit] = useState(false);

  // Load overlay configurations and merge with project configs so overlay features
  // appear in the FeaturesPopup when multiple features are clicked on the map.
  const { overlays: overlayConfig, overlayLayerConfigs } = useOverlaysWithConfigs(versionId, project);

  const mergedConfig = useMemo((): Configuration => mergeConfigWithOverlays(config, overlayConfig), [config, overlayConfig]);

  const mergedLayerConfigs = useMemo(
    (): Array<LayerConfig | SublayerConfig> => [...layerConfigs, ...overlayLayerConfigs],
    [layerConfigs, overlayLayerConfigs]
  );

  /**
   * Monitor for changes that require the URL to be updated
   */
  useEffect(() => {
    if (!selectedComment && type === "comment" && id) {
      navigate(`/project/${uuid}`);
    }
  }, [selectedComment, featureSelectionPopup]);

  /**
   * If the URL (/project/:uuid/comment/:id) contains a comment ID when the comment is first loaded
   * then select the comment.
   */
  useEffect(() => {
    if (id && type === "comment" && comment && !init) {
      // We set the init value which avoids a race condition where
      // the component mounts prior to comments being loaded
      setInit(true);
      dispatch(selectComment({ commentID: id, features: comment.Features, showPopup: true }));

      if (comment.State === "resolved") {
        // By default resolved comments are not shown - change that
        dispatch(setFilters({ State: ["open", "resolved"], Importance: [], Version: [] }));
      }

      dispatch(setSearchText(comment.ID));
      if (layout?.model) selectWidget(layout.model, Widget.Comments);

      // For non project comments we open up the popup on the map as well
      if (comment.Type !== "project") {
        if (comment.Features.length > 0) {
          const bestPoint = turf.pointOnFeature(comment.Features[0] as AllGeoJSON);
          map?.jumpTo({
            center: bestPoint.geometry.coordinates as [number, number],
          });
        }
      }
    }
  }, []);

  /**
   * Handles selecting a single feature to view the details of.
   * This is called when a user selects one of X layers to view from
   * the FeaturesPopup.
   */
  const onFeatureSelected = useCallback(
    async (feature: mapboxgl.MapboxGeoJSONFeature) => {
      if (feature.properties?.commentID) {
        dispatch(
          selectComment({
            commentID: feature.properties?.commentID,
            features: [feature],
            point: featureSelectionPopup.lngLat,
            isFromFeaturesPopup: true,
          })
        );
      } else {
        const sourceVersionId = (feature.layer as { metadata?: { versionId?: string } })?.metadata?.versionId;
        dispatch(selectFeature(feature, featureSelectionPopup.lngLat, true, sourceVersionId));
        if (layout?.model) selectWidget(layout.model, Widget.Properties);
      }
    },
    [dispatch, featureSelectionPopup, layout]
  );

  return (
    <>
      {featureSelectionPopup && (
        <FeaturesPopup
          features={featureSelectionPopup.features}
          lngLat={featureSelectionPopup.lngLat}
          layerConfigs={mergedLayerConfigs}
          config={mergedConfig}
          onClose={onClose}
          onFeatureClick={onFeatureSelected}
        />
      )}
      {/*
        The CommentPopup is shown only when a comment has been selected & flagged with showPopup.
        If editMode === "comment" it means we are currently drawing a feature and the popup should
        be hidden
      */}
      {selectedComment?.lngLat && selectedComment.showPopup && <CommentPopup onClose={onClose} hidden={editMode === "comment"} />}

      {/*
        If the user is editing a feature present the Comment Feature Editor to the user
      */}
      {editId && selectedComment?.commentID && <CommentFeatureEditor commentId={selectedComment?.commentID} />}
    </>
  );
};

export default FeatureHandler;
